import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/ui/atoms/Button';
import Eyebrow from '@/ui/atoms/Eyebrow';
import { useReducedMotion } from '@/hooks/useReducedMotion';

// CouncilPreview — P1 step 0 streaming sample (15s fixture).
// Spec: state_machines/onboarding.yaml § preview.
// Renders a deterministic 4-line dialogue progressively. SkipCta is always
// available; ERROR_FIXTURE_LOAD is impossible because the fixture is inline.

interface PreviewLine {
  speaker_label_key: string; // e.g. demo.compass.role
  speaker_quote_key: string; // e.g. demo.compass.text
  delay_ms: number;
}

const PREVIEW_LINES: PreviewLine[] = [
  { speaker_label_key: 'demo.compass.role', speaker_quote_key: 'demo.compass.text', delay_ms: 0 },
  { speaker_label_key: 'demo.iron.role', speaker_quote_key: 'demo.iron.text', delay_ms: 4500 },
  { speaker_label_key: 'demo.sage.role', speaker_quote_key: 'demo.sage.text', delay_ms: 9000 },
];

export interface CouncilPreviewProps {
  onComplete: () => void;
  onSkip: () => void;
}

export function CouncilPreview({ onComplete, onSkip }: CouncilPreviewProps) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const [shown, setShown] = useState<number>(reducedMotion ? PREVIEW_LINES.length : 1);
  const completedRef = useRef(false);

  useEffect(() => {
    if (reducedMotion) {
      // Show all lines instantly; trigger completion shortly after.
      const id = window.setTimeout(() => {
        if (!completedRef.current) {
          completedRef.current = true;
          onComplete();
        }
      }, 800);
      return () => window.clearTimeout(id);
    }
    const timers = PREVIEW_LINES.map((line, i) =>
      window.setTimeout(() => setShown((s) => Math.max(s, i + 1)), line.delay_ms),
    );
    const completeTimer = window.setTimeout(() => {
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete();
      }
    }, 14000);
    timers.push(completeTimer);
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [reducedMotion, onComplete]);

  return (
    <section
      className="px-8 py-8 border border-hairline rounded-md bg-paper"
      data-testid="council-preview"
    >
      <div className="flex items-baseline justify-between mb-4">
        <Eyebrow tone="vermilion">{t('ahaOnboarding.preview.title')}</Eyebrow>
        <button
          type="button"
          onClick={onSkip}
          className="text-sm text-ink-3 hover:text-ink underline-offset-2 hover:underline"
          data-testid="council-preview-skip"
        >
          {t('ahaOnboarding.preview.skipCta')}
        </button>
      </div>
      <p className="text-ink-2 mb-6 leading-relaxed">{t('ahaOnboarding.preview.sub')}</p>
      <ol className="space-y-4">
        {PREVIEW_LINES.slice(0, shown).map((line, i) => (
          <li
            key={i}
            className="px-4 py-3 border-l-2 border-vermilion bg-canvas/40 rounded-r-md"
            data-testid={`council-preview-line-${i}`}
          >
            <div className="font-mono text-[10px] tracking-meta uppercase text-ink-3 mb-1">
              {t(line.speaker_label_key)}
            </div>
            <p className="text-ink leading-relaxed text-sm">
              {t(line.speaker_quote_key) as string}
            </p>
          </li>
        ))}
      </ol>
      <div className="mt-6 flex justify-end">
        <Button variant="ghost" onClick={onSkip}>
          {t('ahaOnboarding.preview.skipCta')}
        </Button>
      </div>
    </section>
  );
}

export default CouncilPreview;
